import React from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import {
  Input as InputIcon,
  Transform as TransformIcon,
  Psychology as AiIcon,
  Output as OutputIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { MODULE_TYPES, NODE_COLORS } from '../../utils/constants';
import { CODE_MODULE, isCodeModule } from '../modules/codeModules';

const CODE_GROUP = 'code';

const NodeSelector = ({ modules = [], selectedType, onSelect, onAddNode }) => {
  const getModulesByType = (type) => {
    if (!modules || !Array.isArray(modules)) return [];

    // Code modules get their own group; the bare python_code built-in is only
    // useful through a code module, so it isn't offered on its own.
    if (type === CODE_GROUP) return modules.filter(isCodeModule);
    return modules.filter(module => module.type === type && module.module_name !== CODE_MODULE);
  };
  
  const getIconForType = (type) => {
    switch (type) {
      case MODULE_TYPES.INPUT:
        return <InputIcon />;
      case MODULE_TYPES.TRANSFORM:
        return <TransformIcon />;
      case MODULE_TYPES.AI_ENRICHMENT:
        return <AiIcon />;
      case MODULE_TYPES.CUSTOM_CODE:
      case CODE_GROUP:
        return <CodeIcon />;
      case MODULE_TYPES.OUTPUT:
        return <OutputIcon />;
      default:
        return null;
    }
  };
  
  const onDragStart = (event, module) => {
    const dragData = {
      type: module.type,
      module: module.module_name,
      name: module.name,
      id: module.id,
      source: module.source || 'built_in',
      customModuleId: module.custom_module_id || null,
      presetConfig: module.preset_config || {},
    };
    event.dataTransfer.setData('application/reactflow', JSON.stringify(dragData));
    event.dataTransfer.effectAllowed = 'move';
  };
  
  const renderModuleList = (type, label) => {
    if (selectedType && type !== selectedType && !(type === CODE_GROUP && selectedType === MODULE_TYPES.TRANSFORM)) {
      return null;
    }
    
    const moduleList = getModulesByType(type);
    if (moduleList.length === 0) return null;
    
    return (
      <Box key={type} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
          {label}
        </Typography>
        <List dense>
          {moduleList.map((module) => (
            <ListItem key={module.id} disablePadding>
              <ListItemButton
                draggable
                onDragStart={(event) => onDragStart(event, module)}
                onClick={() => {
                  if (onAddNode) {
                    onAddNode(module);
                  }
                  if (onSelect) onSelect();
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: NODE_COLORS[module.type].background,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {getIconForType(type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      {module.name}
                      {module.source === 'custom' && type !== CODE_GROUP && (
                        <Chip component="span" label="Custom" size="small" color="primary" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  }
                  secondary={module.description}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: {
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
      </Box>
    );
  };
  
  return (
    <Box sx={{ overflow: 'auto', height: '100%' }}>
      {renderModuleList(MODULE_TYPES.INPUT, 'Input Modules')}
      {renderModuleList(MODULE_TYPES.TRANSFORM, 'Transform Modules')}
      {renderModuleList(CODE_GROUP, 'Code')}
      {renderModuleList(MODULE_TYPES.AI_ENRICHMENT, 'AI Enrichment Modules')}
      {renderModuleList(MODULE_TYPES.CUSTOM_CODE, 'Custom Code Modules')}
      {renderModuleList(MODULE_TYPES.OUTPUT, 'Output Modules')}
      
      {/* Show placeholder when no modules are available */}
      {(!modules || !Array.isArray(modules) || modules.length === 0) && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="body2" color="text.secondary">
              No modules available. Please check your module configurations.
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default NodeSelector;
